/**
 * @author Nihar <nihar.baishya@314ecorp.com>
 * @description Component for StatusBadge
 */
import _ from 'lodash';
import { Tag, Tooltip, Space, Badge, Badge as AntBadge, Typography, Flex } from 'antd';
import { ReduceOne, Info } from '@icon-park/react';
import { WarningOutlined } from '@ant-design/icons';

import { batchCompletionPercentage } from 'utils/eligibility/eligibilityBatchUpload';
import { getDateStringInPracticeZone } from 'utils/dateParser';
import {
	ELIGIBILITY_AUTH_INDICATOR,
	ELIGIBILITY_BATCH_UPLOAD_STATUS,
	ELIGIBILITY_BATCH_UPLOAD_STATUS_CODES,
	ELIGIBILITY_COVERAGE_LEVEL_COLORS,
	ELIGIBILITY_NETWORK_STATUS,
	ELIGIBILITY_PAYER_ENROLLMENT_STATUS,
	ELIGIBILITY_PAYER_ENROLLMENT_TAG_STATUS,
	ELIGIBILITY_PAYER_STATUS,
	ELIGIBILITY_PAYER_TAG_STATUS,
	ELIGIBILITY_RESPONSE_TAG_STATUS,
} from 'app-constants/eligibility';
import { SERVICE_STATUS_CODES } from 'app-constants/eligibility/benefit';
import { CLAIM_STATUS_TAG_COLORS } from 'app-constants/claims';
import { ANT_TAG_COLORS } from '@veritable/common/constants/antd';
import { WORKLIST_PRIORITIES, WORKLIST_TYPE_TOOLTIP_TEXT, WORKLIST_TYPES } from 'app-constants/claims/workLists';
import { CLAIM_WORK_STATES } from 'app-constants/claims/claimRequest';
import { CLEARING_HOUSE_STATUS, CLEARING_HOUSE_TRANSACTION_TYPE } from 'app-constants/admin/clearinghouse';
import { CLAIM_STATUS_CODES_CLASS_NAMES, CLAIM_STATUS_CODES_TYPES } from 'app-constants/claims/claimStatusCodes';

const CLAIM_STATUS_CODE_TYPE_ICON_MAPPING = {
	[CLAIM_STATUS_CODES_TYPES.PAID]: '',
	[CLAIM_STATUS_CODES_TYPES.IN_PROCESS]: '',
	[CLAIM_STATUS_CODES_TYPES.INCOMPLETE]: '',
	[CLAIM_STATUS_CODES_TYPES.COMPLETED]: '',
	[CLAIM_STATUS_CODES_TYPES.ERROR]: <WarningOutlined style={{ color: '#595959' }} />,
};

const BadgeComponent: React.FC<EligibilityResponse.IBadgeProps> = ({ label, color, style, title, className, ...props }) => {
	return (
		<Tooltip classNames={{ root: 'custom-tooltip' }} title={title || ''}>
			<Tag color={color} className={className} {...props} style={style}>
				{label}
			</Tag>
		</Tooltip>
	);
};
const ResponseStatusBadge: React.FC<EligibilityResponse.IResponseStatusBadgeProps> = ({ status, shouldShowParentCategory, ...props }) => {
	const { tagColor, label, parentCategory } = _.get(
		ELIGIBILITY_RESPONSE_TAG_STATUS,
		_.toString(status),
		ELIGIBILITY_RESPONSE_TAG_STATUS.unknown,
	);
	return <BadgeComponent title='Response Status' label={shouldShowParentCategory ? parentCategory : label} color={tagColor} {...props} />;
};

const ServiceStatusBadge: React.FC<{ status: string }> = ({ status }) => {
	const { color, label } = _.get(SERVICE_STATUS_CODES, _.toString(status), ELIGIBILITY_RESPONSE_TAG_STATUS.unknown);
	return <BadgeComponent title='Response Status' label={label} color={color} />;
};

const NetworkBadge: React.FC<EligibilityResponse.INetworkBadgeProps> = ({ status }) => {
	const { label, tagColor } = _.get(ELIGIBILITY_NETWORK_STATUS, _.toString(status), ELIGIBILITY_RESPONSE_TAG_STATUS.unknown);
	return <BadgeComponent title='Network' label={label} color={tagColor} />;
};

const MemberIdBadge: React.FC<{ memberId?: string | null }> = ({ memberId }) => {
	if (!memberId) {
		return null;
	}
	return <BadgeComponent title='Member ID' label={`Member ID: ${memberId}`} color={ANT_TAG_COLORS.default} />;
};

const CoverageBadge: React.FC<EligibilityResponse.ICoverageBadgeProps> = ({ coverageTitle = '' }) => {
	const color = _.get(ELIGIBILITY_COVERAGE_LEVEL_COLORS, [coverageTitle]);
	return (
		<BadgeComponent
			title='Coverage'
			label={_.upperFirst(coverageTitle) || ELIGIBILITY_RESPONSE_TAG_STATUS.unknown.label}
			color={color || ANT_TAG_COLORS.default}
		/>
	);
};

const AuthorizationBadge: React.FC<EligibilityResponse.IAuthBadgeProps> = ({ status }) => {
	const { label, tagColor } = _.get(ELIGIBILITY_AUTH_INDICATOR, [status], ELIGIBILITY_RESPONSE_TAG_STATUS.unknown);
	return <BadgeComponent title='Authorization' label={label} color={tagColor} />;
};

const BatchUploadStatusBadge: React.FC<{
	status?: EligibilityBatchUpload.IBatchTaskStatus;
	batchMeta?: EligibilityBatchUpload.IBatchEligibilityResponseList;
	style?: React.CSSProperties;
	'data-testid'?: string;
}> = ({ status, batchMeta, style, 'data-testid': dataTestId }) => {
	let batchStatus = batchMeta?.status;
	if (_.isNumber(status)) {
		batchStatus = status;
	}
	const meta = _.get(ELIGIBILITY_BATCH_UPLOAD_STATUS, batchStatus, ELIGIBILITY_RESPONSE_TAG_STATUS.unknown);
	const tooltipTitle = _.get(meta, 'tooltip');
	const isInProgress = batchStatus === ELIGIBILITY_BATCH_UPLOAD_STATUS_CODES.inProgress;
	const validTotal = (batchMeta?.metadata?.total || 0) - (batchMeta?.metadata?.skipped || 0);
	const done = batchMeta?.metadata?.done || 0;
	const percent = batchCompletionPercentage(validTotal, done);
	const badgeStyle = isInProgress
		? { background: `linear-gradient(to right, #b5cbfc ${percent}%, transparent 0% )`, ...(style || {}) }
		: { ...(style || {}) };
	const scheduledTime = batchMeta?.scheduledTime ? getDateStringInPracticeZone(batchMeta?.scheduledTime) : undefined;
	const badgeTooltip = isInProgress ? `${done} of ${validTotal} completed (${percent}%)` : scheduledTime || '';

	const badgeIcon = tooltipTitle ? (
		<Tooltip title={tooltipTitle}>
			<Info theme='filled' style={{ cursor: 'pointer', marginLeft: 4 }} size={12} />
		</Tooltip>
	) : null;
	return (
		<>
			<Tooltip title={badgeTooltip}>
				<BadgeComponent data-testid={dataTestId} title={''} label={meta?.label} color={meta?.tagColor} style={badgeStyle} closeIcon={badgeIcon} />
			</Tooltip>
		</>
	);
};

const ClaimStatusBadge: React.FC<ClaimStatusRequests.IClaimStatusBadgeProps> = ({ status, isClaimStatusSupported }) => {
	const meta = _.get(CLAIM_STATUS_TAG_COLORS, status);
	const style = { width: 'fit-content' };
	if (isClaimStatusSupported === false) {
		return <BadgeComponent style={style} title='' label='Payer Not Supported' color={ANT_TAG_COLORS.default} />;
	}

	return (
		<BadgeComponent
			style={style}
			title={meta?.title || ''}
			label={meta?.label || ELIGIBILITY_RESPONSE_TAG_STATUS.unknown.label}
			className={_.get(CLAIM_STATUS_CODES_CLASS_NAMES, meta?.type)}
			closeIcon={_.get(CLAIM_STATUS_CODE_TYPE_ICON_MAPPING, meta?.type)}
		/>
	);
};

const ClaimWorkStateBadge: React.FC<
	Record<'workState', ClaimRequest.IClaimWorkState> & { showIcon?: boolean; labelClassName?: string }
> = ({ workState, showIcon = false, labelClassName }) => {
	const meta = _.get(CLAIM_WORK_STATES, workState);
	return (
		<Flex gap={8}>
			<AntBadge
				size='small'
				color={meta?.tagColor || '#595959'}
				text={
					<Typography.Text className={labelClassName} style={{ color: meta?.textColor || '#595959' }}>
						{meta?.label || ELIGIBILITY_RESPONSE_TAG_STATUS.unknown.label}
					</Typography.Text>
				}
			/>
			{showIcon && (
				<Tooltip title='Void Claim'>
					<ReduceOne theme='filled' size='16' fill='#FF6B39' />
				</Tooltip>
			)}
		</Flex>
	);
};

const WorkListTypeBadge: React.FC<{ type: ClaimWorkLists.IWorkListType }> = ({ type }) => {
	const tagMeta = _.get(WORKLIST_TYPES, type, ELIGIBILITY_RESPONSE_TAG_STATUS.unknown);
	return (
		<BadgeComponent
			title={WORKLIST_TYPE_TOOLTIP_TEXT[type]}
			label={tagMeta.label}
			color={tagMeta.tagColor}
			style={_.get(tagMeta, 'tagStyle')}
		/>
	);
};

const PayerStatusBadge: React.FC<{ payerStatus: Payers.IPayerStatus }> = ({ payerStatus }) => {
	const { tagColor, label } = _.get(ELIGIBILITY_PAYER_STATUS, _.toString(payerStatus), ELIGIBILITY_PAYER_TAG_STATUS.unknown);
	return <BadgeComponent title='Payer Status' label={label} color={tagColor} />;
};

const PayerBadge: React.FC<{
	isEligibility: boolean;
	isClaimStatus: boolean;
	isEnrollmentRequired?: boolean;
}> = ({ isEligibility, isClaimStatus, isEnrollmentRequired }) => {
	return (
		<Space>
			{isClaimStatus && <Tag className='border-[#B37FEB] bg-[#F9F0FF] text-[#B37FEB]'>{'Claim Checks'}</Tag>}
			{isEligibility && <Tag className='border-[#EB2F96] bg-[#FFF0F6] text-[#EB2F96]'>{'Eligibility'}</Tag>}
			{isEnrollmentRequired && <Tag className='border-[#BFBFBF] bg-[#F0F0F0] text-[#7A7777]'>{'Enrollment Required'}</Tag>}
		</Space>
	);
};

const EnrollmentStatusBadge: React.FC<{
	enrollmentStatus: Payers.IEnrollmentStatus;
}> = ({ enrollmentStatus }) => {
	const { tagColor, label } =
		_.get(ELIGIBILITY_PAYER_ENROLLMENT_STATUS, [enrollmentStatus]) || ELIGIBILITY_PAYER_ENROLLMENT_TAG_STATUS.unknown;
	return <BadgeComponent title='Enrollment Status' label={label} color={tagColor} />;
};

const PriorityBadge: React.FC<{ priority?: string }> = ({ priority }) => {
	if (!priority) {
		return null;
	}
	const { label, color } = _.get(WORKLIST_PRIORITIES, priority, ELIGIBILITY_RESPONSE_TAG_STATUS.unknown);
	return (
		<Tag className='border-dashed' icon={<Badge color={color} className='text-xxs mr-1' />}>
			{label}
		</Tag>
	);
};

const ClearingHouseStatusBadge: React.FC<{ status: ClearingHouse.ClearingHouseStatus }> = ({ status }) => {
	const { label, tagColor } =
		_.find(CLEARING_HOUSE_STATUS, (value) => value?.value === status) || ELIGIBILITY_RESPONSE_TAG_STATUS.unknown;
	return <BadgeComponent title='Clearing House Status' label={label} color={tagColor} />;
};

const ClearingHouseTransactionBadge: React.FC<{ transaction: ClearingHouse.TransactionType }> = ({ transaction }) => {
	const { label, tagColor } =
		_.find(CLEARING_HOUSE_TRANSACTION_TYPE, (value) => _.includes(value?.value, transaction)) ||
		ELIGIBILITY_RESPONSE_TAG_STATUS.unknown;
	return <BadgeComponent title='Clearing House Status' label={label} color={tagColor} />;
};

export {
	BadgeComponent,
	ResponseStatusBadge,
	AuthorizationBadge,
	CoverageBadge,
	ServiceStatusBadge,
	NetworkBadge,
	MemberIdBadge,
	BatchUploadStatusBadge,
	ClaimStatusBadge,
	PayerBadge,
	PayerStatusBadge,
	EnrollmentStatusBadge,
	WorkListTypeBadge,
	ClaimWorkStateBadge,
	PriorityBadge,
	ClearingHouseStatusBadge,
	ClearingHouseTransactionBadge,
};
